/**
 * CSS Selector Utilities
 *
 * Pure utility functions for building and manipulating CSS selectors
 * used in the scraper preview inspector.
 */

export const SCRAPER_OUTLINE_CLASS = 'scraper-preview-outline';
export const SCRAPER_INSPECTOR_CLASS = 'scraper-preview-inspector';
export const INTERNAL_PREVIEW_CLASSES = new Set([
  SCRAPER_OUTLINE_CLASS,
  SCRAPER_INSPECTOR_CLASS,
]);

/**
 * Filters out internal preview classes from an element's classList
 */
export const getFilteredClasses = (element: Element): string[] =>
  Array.from(element.classList).filter(
    (className) => className && !INTERNAL_PREVIEW_CLASSES.has(className)
  );

/**
 * Escapes special characters in CSS identifiers
 */
export const escapeCssIdentifier = (value: string): string => {
  if (typeof window !== 'undefined' && window.CSS?.escape) {
    return window.CSS.escape(value);
  }

  return value.replace(/([^\w-])/g, '\\$1');
};

/**
 * Gets all sibling elements with the same tag name
 */
export const getSameTagSiblings = (element: Element): Element[] => {
  const parent = element.parentElement;
  if (!parent) {
    return [];
  }
  return Array.from(parent.children).filter(
    (child) => child.tagName === element.tagName
  );
};

/**
 * Gets the nth-of-type index for an element (1-based)
 */
export const getNthOfType = (element: Element): number | null => {
  const siblings = getSameTagSiblings(element);
  if (siblings.length <= 1) {
    return null;
  }
  return siblings.indexOf(element) + 1;
};

/**
 * Builds a CSS selector using element classes
 */
export const buildClassSelector = (element: Element): string | null => {
  const classes = getFilteredClasses(element);
  if (classes.length === 0) {
    return null;
  }
  const limitedClasses = classes.slice(0, 3).map(escapeCssIdentifier);
  return `${element.tagName.toLowerCase()}.${limitedClasses.join('.')}`;
};

/**
 * Builds a CSS selector path from element to ancestor
 */
export const buildAncestorSelector = (
  element: Element,
  options?: { includeNth?: boolean; maxDepth?: number }
): string => {
  const maxDepth = options?.maxDepth ?? 5;
  const includeNth = options?.includeNth ?? false;
  const segments: string[] = [];
  let depth = 0;
  let current: Element | null = element;

  while (current && depth < maxDepth) {
    if (current.id) {
      segments.unshift(`#${escapeCssIdentifier(current.id)}`);
      break;
    }

    let segment = current.tagName.toLowerCase();
    const classes = getFilteredClasses(current)
      .slice(0, 2)
      .map(escapeCssIdentifier);
    if (classes.length) {
      segment += `.${classes.join('.')}`;
    } else if (includeNth) {
      const nth = getNthOfType(current);
      if (nth) {
        segment += `:nth-of-type(${nth})`;
      }
    }

    segments.unshift(segment);
    depth += 1;
    current = current.parentElement;
  }

  const limitedSegments = segments.slice(-3);
  return limitedSegments.join(' > ');
};

/**
 * Creates a human-readable description of an element
 */
export const describeElement = (element: Element): string => {
  const tag = element.tagName.toLowerCase();
  const idPart = element.id ? `#${element.id}` : '';
  const classList = getFilteredClasses(element);
  const classPart = classList.length ? `.${classList.join('.')}` : '';
  return `${tag}${idPart}${classPart}`;
};

/**
 * Builds a breadcrumb path from element to root
 */
export interface InspectorBreadcrumbItem {
  label: string;
  element: Element;
}

export const buildElementPath = (element: Element): InspectorBreadcrumbItem[] => {
  const segments: InspectorBreadcrumbItem[] = [];
  let current: Element | null = element;
  while (current) {
    segments.unshift({
      label: describeElement(current),
      element: current,
    });
    current = current.parentElement;
  }
  return segments;
};

/**
 * Gets the number of elements matching a CSS selector
 */
export const getCssMatchCount = (selector: string, doc: Document): number | null => {
  try {
    return doc.querySelectorAll(selector).length;
  } catch (error) {
    console.warn('Failed to evaluate selector', selector, error);
    return null;
  }
};

/**
 * Suggestion for a CSS selector
 */
export interface InspectorSuggestion {
  id: string;
  label: string;
  value: string;
  type: 'css';
  matches: number | null;
}

/**
 * Builds a list of suggested CSS selectors for an element
 */
export const buildInspectorSuggestions = (
  element: Element,
  doc: Document
): InspectorSuggestion[] => {
  const cssSuggestions: InspectorSuggestion[] = [];
  const pushCssSuggestion = (suggestion: InspectorSuggestion) => {
    if (!cssSuggestions.find((item) => item.value === suggestion.value)) {
      cssSuggestions.push(suggestion);
    }
  };

  if (element.id) {
    const idSelector = `#${escapeCssIdentifier(element.id)}`;
    pushCssSuggestion({
      id: 'css-id',
      label: 'ID selector',
      value: idSelector,
      type: 'css',
      matches: getCssMatchCount(idSelector, doc),
    });
  }

  const classSelector = buildClassSelector(element);
  if (classSelector) {
    pushCssSuggestion({
      id: 'css-class',
      label: 'Tag + class',
      value: classSelector,
      type: 'css',
      matches: getCssMatchCount(classSelector, doc),
    });
  }

  const shortPath = buildAncestorSelector(element, {
    includeNth: false,
    maxDepth: 4,
  });
  if (shortPath) {
    pushCssSuggestion({
      id: 'css-short',
      label: 'Short path',
      value: shortPath,
      type: 'css',
      matches: getCssMatchCount(shortPath, doc),
    });
  }

  const nthPath = buildAncestorSelector(element, {
    includeNth: true,
    maxDepth: 5,
  });
  if (nthPath) {
    pushCssSuggestion({
      id: 'css-nth',
      label: 'nth-of-type path',
      value: nthPath,
      type: 'css',
      matches: getCssMatchCount(nthPath, doc),
    });
  }

  const cssPriority: Record<string, number> = {
    'css-id': 1,
    'css-short': 2,
    'css-nth': 3,
    'css-class': 4,
  };
  return [...cssSuggestions]
    .sort(
      (a, b) =>
        (cssPriority[a.id] ?? Number.MAX_SAFE_INTEGER) -
        (cssPriority[b.id] ?? Number.MAX_SAFE_INTEGER)
    )
    .slice(0, 3);
};
